"use client";

import { Button, Heading, Icon, Image, Tag } from "@pamperedchef/design-system";
import { RichText } from "@/composites/foundation";
import { useLocale } from "next-intl";
import type { Cta } from "@/composites/landing/types";
import { CheckList } from "@/composites/landing/CheckList";
import { PriceDisplay } from "@/composites/product/PriceDisplay";
import * as styles from "./OfferCard.module.scss";

/**
 * Best-effort parse of CMS price strings (e.g. `"0"`, `"299"`, `"$299.00"`)
 * into a numeric value for the `PriceDisplay` composite. Returns `undefined`
 * when the string can't be coerced — caller renders nothing in that case.
 */
function parsePriceText(text: string | undefined): number | undefined {
	if (!text) return undefined;
	const cleaned = text.replace(/[^0-9.\-]/g, "");
	if (!cleaned) return undefined;
	const value = Number.parseFloat(cleaned);
	return Number.isFinite(value) ? value : undefined;
}

export interface OfferCardProps {
	sectionTitle?: string;
	badgeText?: string;
	title: string;
	priceText?: string;
	originalPriceText?: string;
	description?: string;
	features?: string[];
	disclaimer?: { title?: string; body?: string };
	cta?: Cta;
	image?: { url: string; alt?: string };
}

/**
 * Featured exclusive-offer card — the VIP free-kit hero offer. Cream card,
 * two-column on desktop (image left, content right): a teal "Free Kit" `Tag`,
 * a teal serif title, a price row (bold current + struck-through original),
 * description, divider, a feature `CheckList`, a white info note, and a yellow
 * filled CTA. Presentational, props-in, RSC-safe.
 */
export function OfferCard({
	sectionTitle,
	badgeText,
	title,
	priceText,
	originalPriceText,
	description,
	features,
	disclaimer,
	cta,
	image
}: OfferCardProps) {
	const locale = useLocale();
	// Derive currency from the active locale rather than hardcoding it — the
	// store serves en-US (USD) and en-CA (CAD).
	const currency = locale.toLowerCase().endsWith("ca") ? "CAD" : "USD";
	const currentPrice = parsePriceText(priceText);
	const originalPrice = parsePriceText(originalPriceText);

	return (
		<div
			className={styles["offer-card-section"]}
			data-component="OfferCard"
		>
			{sectionTitle ? (
				<Heading
					tag="h2"
					className={styles["offer-card__section-title"]}
				>
					{sectionTitle}
				</Heading>
			) : null}

			<section className={styles["offer-card"]}>
				{image ? (
					<div className={styles["offer-card__media"]}>
						<Image
							src={image.url}
							alt={image.alt ?? title}
							className={styles["offer-card__image"]}
							objectFit="cover"
						/>
					</div>
				) : null}

				<div className={styles["offer-card__body"]}>
					<div className={styles["offer-card__header"]}>
						{badgeText ? (
							<Tag
								kind="subtle"
								color="emerald"
								size="md"
								label={badgeText}
								className={styles["offer-card__badge"]}
							/>
						) : null}

						<Heading
							tag="h3"
							className={styles["offer-card__title"]}
						>
							{title}
						</Heading>

						{currentPrice !== undefined ? (
							<PriceDisplay
								display={currentPrice}
								strikeThrough={originalPrice}
								currency={currency}
								locale={locale}
								size="large"
								className={styles["offer-card__price"]}
							/>
						) : null}

						{description ? (
							<RichText
								content={description}
								className={styles["offer-card__description"]}
							/>
						) : null}

						{features && features.length > 0 ? (
							<hr className={styles["offer-card__divider"]} />
						) : null}
					</div>

					{features && features.length > 0 ? (
						<CheckList items={features} />
					) : null}

					{disclaimer && (disclaimer.title || disclaimer.body) ? (
						<aside className={styles["offer-card__disclaimer"]}>
							<span
								className={
									styles["offer-card__disclaimer-icon"]
								}
								aria-hidden="true"
							>
								<Icon name="info" size={24} />
							</span>
							<span
								className={
									styles["offer-card__disclaimer-text"]
								}
							>
								{disclaimer.title ? (
									<span
										className={
											styles[
												"offer-card__disclaimer-title"
											]
										}
									>
										{disclaimer.title}
									</span>
								) : null}
								{disclaimer.body ? (
									<span
										className={
											styles[
												"offer-card__disclaimer-body"
											]
										}
									>
										{disclaimer.body}
									</span>
								) : null}
							</span>
						</aside>
					) : null}

					{cta ? (
						<Button
							kind="primary"
							size="lg"
							onPress={() => {
								window.location.href = cta.href;
							}}
							className={styles["offer-card__cta"]}
						>
							{cta.title}
						</Button>
					) : null}
				</div>
			</section>
		</div>
	);
}
